import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { PermissionRoute } from './auth/PermissionRoute';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { FullPageLoader } from './components/ui/FullPageLoader';
import { AppShell } from './layout/AppShell';
import { ClientsPage } from './pages/ClientsPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';

// Routes a coach doesn't hit on first paint are code-split so the initial
// bundle stays small; ClientsPage/DashboardPage/LoginPage stay eager since
// they're reached immediately after login.
const ClientDetailPage = lazy(() =>
  import('./pages/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })),
);
const TemplatesPage = lazy(() =>
  import('./pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const HtmlTemplateEditorPage = lazy(() =>
  import('./pages/HtmlTemplateEditorPage').then((m) => ({ default: m.HtmlTemplateEditorPage })),
);
const WhatsNewPage = lazy(() =>
  import('./pages/WhatsNewPage').then((m) => ({ default: m.WhatsNewPage })),
);
const AvailabilityPage = lazy(() =>
  import('./pages/AvailabilityPage').then((m) => ({ default: m.AvailabilityPage })),
);
const BookingsPage = lazy(() =>
  import('./pages/BookingsPage').then((m) => ({ default: m.BookingsPage })),
);
const TodayBookingsPage = lazy(() =>
  import('./pages/TodayBookingsPage').then((m) => ({ default: m.TodayBookingsPage })),
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const NewAssessmentPage = lazy(() =>
  import('./pages/NewAssessmentPage').then((m) => ({ default: m.NewAssessmentPage })),
);
const NewQuestionnairePage = lazy(() =>
  import('./pages/NewQuestionnairePage').then((m) => ({ default: m.NewQuestionnairePage })),
);
const QuestionnaireTemplateEditorPage = lazy(() =>
  import('./pages/QuestionnaireTemplateEditorPage').then((m) => ({
    default: m.QuestionnaireTemplateEditorPage,
  })),
);
const LeadsPage = lazy(() => import('./pages/LeadsPage').then((m) => ({ default: m.LeadsPage })));
const PaymentsPage = lazy(() =>
  import('./pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const QuestionnairesPage = lazy(() =>
  import('./pages/QuestionnairesPage').then((m) => ({ default: m.QuestionnairesPage })),
);
const ReportsPage = lazy(() =>
  import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const ReportDetailPage = lazy(() =>
  import('./pages/ReportDetailPage').then((m) => ({ default: m.ReportDetailPage })),
);
const ReportEditPage = lazy(() =>
  import('./pages/reports/edit/ReportEditPage').then((m) => ({ default: m.ReportEditPage })),
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const TeamPage = lazy(() => import('./pages/TeamPage').then((m) => ({ default: m.TeamPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((m) => ({ default: m.RolesPage })));
const AuditLogPage = lazy(() =>
  import('./pages/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);
const PackagesPage = lazy(() =>
  import('./pages/PackagesPage').then((m) => ({ default: m.PackagesPage })),
);
const BankAccountsPage = lazy(() =>
  import('./pages/BankAccountsPage').then((m) => ({ default: m.BankAccountsPage })),
);
const ReportSectionsPage = lazy(() =>
  import('./pages/ReportSectionsPage').then((m) => ({ default: m.ReportSectionsPage })),
);
const ResourcesPage = lazy(() =>
  import('./pages/ResourcesPage').then((m) => ({ default: m.ResourcesPage })),
);
const VerifyEmailChangePage = lazy(() =>
  import('./pages/VerifyEmailChangePage').then((m) => ({ default: m.VerifyEmailChangePage })),
);

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/verify-email-change"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <VerifyEmailChangePage />
          </Suspense>
        }
      />

      {/* Everything below requires an authenticated coach. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route element={<PermissionRoute permission="clients:view" />}>
            <Route path="clients" element={<ClientsPage />} />
            <Route
              path="clients/:clientId"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ClientDetailPage />
                </Suspense>
              }
            />
            <Route element={<PermissionRoute permission="assessments:create" />}>
              <Route
                path="clients/:clientId/assessments/new"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <NewAssessmentPage />
                  </Suspense>
                }
              />
            </Route>
            <Route element={<PermissionRoute permission="questionnaires:dispatch" />}>
              <Route
                path="clients/:clientId/questionnaires/new"
                element={
                  <Suspense fallback={<FullPageLoader />}>
                    <NewQuestionnairePage />
                  </Suspense>
                }
              />
            </Route>
          </Route>
          <Route element={<PermissionRoute permission="leads:view" />}>
            <Route
              path="leads"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <LeadsPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="payments:view" />}>
            <Route
              path="payments"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <PaymentsPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="questionnaires:view" />}>
            <Route
              path="questionnaires"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <QuestionnairesPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="questionnaires:dispatch" />}>
            <Route
              path="questionnaires/new"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <NewQuestionnairePage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="reports:view" />}>
            <Route
              path="reports"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ReportsPage />
                </Suspense>
              }
            />
            <Route
              path="reports/:reportId"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ReportDetailPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="reports:edit" />}>
            <Route
              path="reports/:reportId/edit"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ReportEditPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="resources:view" />}>
            <Route
              path="resources"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ResourcesPage />
                </Suspense>
              }
            />
            <Route
              path="resources/groups/:groupId"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ResourcesPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="templates"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <TemplatesPage />
              </Suspense>
            }
          />
          <Route element={<PermissionRoute permission="templates:html-manage" />}>
            <Route
              path="templates/html/new"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <HtmlTemplateEditorPage />
                </Suspense>
              }
            />
            <Route
              path="templates/html/:id/edit"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <HtmlTemplateEditorPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="templates:questionnaire-manage" />}>
            <Route
              path="templates/questionnaire/new"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <QuestionnaireTemplateEditorPage />
                </Suspense>
              }
            />
            <Route
              path="templates/questionnaire/:id/edit"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <QuestionnaireTemplateEditorPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="availability"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <AvailabilityPage />
              </Suspense>
            }
          />
          <Route
            path="bookings"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <BookingsPage />
              </Suspense>
            }
          />
          <Route
            path="bookings/today"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <TodayBookingsPage />
              </Suspense>
            }
          />
          <Route
            path="whats-new"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <WhatsNewPage />
              </Suspense>
            }
          />
          <Route
            path="profile"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <ProfilePage />
              </Suspense>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route element={<PermissionRoute permission="team:view" />}>
            <Route
              path="team"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <TeamPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="roles:manage" />}>
            <Route
              path="team/roles"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <RolesPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="audit:view" />}>
            <Route
              path="audit-log"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <AuditLogPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<PermissionRoute permission="settings:workspace-manage" />}>
            <Route
              path="settings/packages"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <PackagesPage />
                </Suspense>
              }
            />
            <Route
              path="settings/bank-accounts"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <BankAccountsPage />
                </Suspense>
              }
            />
            <Route
              path="settings/report-sections"
              element={
                <Suspense fallback={<FullPageLoader />}>
                  <ReportSectionsPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="*"
            element={
              <Suspense fallback={<FullPageLoader />}>
                <NotFoundPage />
              </Suspense>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
